import React, { useEffect, useState } from 'react';
import { useT } from '../../i18n';
import { api, resolveAsset } from '../../api/client';
import { STORY_FONTS, storyFontCss, DEFAULT_STORY_FONT_CSS } from '../../lib/storyFonts';

const LLM_OPTIONS = ['openai', 'anthropic', 'gemini'];
const IMAGE_OPTIONS = ['openai', 'gemini', 'midjourney', 'seedream', 'none'];
const CURRENCIES = ['ILS', 'USD', 'EUR', 'GBP', 'AED', 'SAR'];

function emptyProduct() {
  return { key: '', nameAr: '', priceCents: 0, currency: 'ILS' };
}

// <input type="datetime-local"> works in LOCAL time; the API stores ISO/UTC.
// Convert between the two so the admin edits wall-clock time, not UTC.
function toLocalInput(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}
function fromLocalInput(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

export default function AdminSettings() {
  const t = useT();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [uploadingBanner, setUploadingBanner] = useState(false);

  useEffect(() => {
    api.get('/api/admin/settings').then(setForm).catch(e => setError(e.message));
  }, []);

  if (!form) {
    return error
      ? <div className="admin-page settings-page"><div className="admin-editor-error">{error}</div></div>
      : <div className="loader"><div className="loader-spinner" /></div>;
  }

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setDelivery = (k, v) => setForm(p => ({ ...p, delivery: { ...(p.delivery || {}), [k]: v } }));
  const setPromo = (k, v) => setForm(p => ({ ...p, promo: { ...(p.promo || {}), [k]: v } }));
  const setPayFirst = (k, v) => setForm(p => ({ ...p, payFirstExperiment: { ...(p.payFirstExperiment || {}), [k]: v } }));

  const uploadBanner = async (file) => {
    if (!file) return;
    setError('');
    setUploadingBanner(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { imageUrl } = await api.postForm('/api/admin/settings/promo-image', fd);
      setPromo('imageUrl', imageUrl);
    } catch (e) {
      setError(e.message || t.adminImageUploadFailed);
    } finally {
      setUploadingBanner(false);
    }
  };
  const setProduct = (i, k, v) => setForm(p => ({
    ...p,
    products: p.products.map((pr, idx) => idx === i ? { ...pr, [k]: v } : pr),
  }));
  const addProduct = () => setForm(p => ({ ...p, products: [...(p.products || []), emptyProduct()] }));
  const removeProduct = (i) => setForm(p => ({ ...p, products: p.products.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setError(''); setSavedMsg('');
    const payload = {
      defaultIterationCapPerScene: Number(form.defaultIterationCapPerScene) || 2,
      gradBannerEnabled: form.gradBannerEnabled !== false,
      storyBodyFont: form.storyBodyFont || 'cairo',
      stripeMode: form.stripeMode,
      skipStripe: !!form.skipStripe,
      llmProvider: form.llmProvider,
      imageProvider: form.imageProvider,
      openaiTextModel: (form.openaiTextModel || '').trim(),
      openaiImageModel: (form.openaiImageModel || '').trim(),
      anthropicModel: (form.anthropicModel || '').trim(),
      geminiModel: (form.geminiModel || '').trim(),
      geminiImageModel: (form.geminiImageModel || '').trim(),
      storyMasterPromptEnabled: !!form.storyMasterPromptEnabled,
      storyMasterPrompt: form.storyMasterPrompt || '',
      products: (form.products || []).map(p => ({
        key: (p.key || '').trim(),
        nameAr: (p.nameAr || '').trim(),
        priceCents: Math.round(Number(p.priceCents) || 0),
        // Display-only "was" price (strikethrough anchor). Null when unset/0.
        compareAtCents: Number(p.compareAtCents) > 0 ? Math.round(Number(p.compareAtCents)) : null,
        currency: p.currency || 'ILS',
      })),
      delivery: {
        amountCents: Math.round(Number(form.delivery?.amountCents) || 0),
        currency: form.delivery?.currency || 'ILS',
      },
      promo: {
        enabled: !!form.promo?.enabled,
        imageUrl: (form.promo?.imageUrl || '').trim(),
        bundleQty: Math.max(1, Math.round(Number(form.promo?.bundleQty) || 2)),
        bundleProductKey: (form.promo?.bundleProductKey || 'hardcover').trim() || 'hardcover',
        bundlePriceCents: Math.round(Number(form.promo?.bundlePriceCents) || 0),
        startsAt: form.promo?.startsAt || null,
        endsAt: form.promo?.endsAt || null,
      },
      payFirstExperiment: {
        enabled: !!form.payFirstExperiment?.enabled,
        percent: Math.max(0, Math.min(100, Math.round(Number(form.payFirstExperiment?.percent) || 0))),
      },
    };

    for (const p of payload.products) {
      if (!p.key) { setError(t.adminProductKeyRequired); return; }
      if (!p.nameAr) { setError(t.adminProductNameRequired); return; }
    }
    const keys = payload.products.map(p => p.key);
    if (new Set(keys).size !== keys.length) { setError(t.adminProductKeysDuplicate); return; }

    setSaving(true);
    try {
      const saved = await api.put('/api/admin/settings', payload);
      setForm(saved);
      setSavedMsg(t.adminSaved);
      setTimeout(() => setSavedMsg(''), 2500);
    } catch (e) {
      setError(e.message || t.adminSaveFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page settings-page">
      <h1 className="page-title">{t.adminSettingsTitle}</h1>

      <section className="settings-card">
        <div className="settings-card-header">
          <h2 className="settings-card-title">Stripe</h2>
          <span className={`settings-mode-pill ${form.stripeMode === 'live' ? 'live' : 'test'}`}>
            {form.stripeMode === 'live' ? '● LIVE' : '● TEST'}
          </span>
        </div>
        <p className="settings-card-help">
          {t.adminStripeHelp}
        </p>
        <div className="settings-grid">
          <label className="settings-field">
            <span>{t.adminStripeMode}</span>
            <select value={form.stripeMode} onChange={e => setField('stripeMode', e.target.value)}>
              <option value="test">Test (Sandbox)</option>
              <option value="live">Live</option>
            </select>
            <span className="settings-field-hint">
              {t.adminStripeKeysHint} {form.stripeMode === 'live' ? 'STRIPE_*_LIVE' : 'STRIPE_*_SANDBOX'}
            </span>
          </label>
          <div className="settings-field" style={{ justifyContent: 'flex-end' }}>
            <label className="settings-toggle-row" style={{ marginTop: 0 }}>
              <input
                type="checkbox"
                checked={!!form.skipStripe}
                onChange={e => setField('skipStripe', e.target.checked)}
              />
              <span>
                <span className="settings-toggle-text">{t.adminSkipStripe}</span>
                <span className="settings-toggle-hint">{t.adminSkipStripeHint}</span>
              </span>
            </label>
          </div>
        </div>
      </section>

      <section className="settings-card">
        <div className="settings-card-header">
          <h2 className="settings-card-title">{t.adminAiProviders}</h2>
        </div>
        <p className="settings-card-help">
          {t.adminAiProvidersHelp}
        </p>
        <div className="settings-grid">
          <label className="settings-field">
            <span>LLM Provider</span>
            <select value={form.llmProvider} onChange={e => setField('llmProvider', e.target.value)}>
              {LLM_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <label className="settings-field">
            <span>Image Provider</span>
            <select value={form.imageProvider} onChange={e => setField('imageProvider', e.target.value)}>
              {IMAGE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
        </div>

        <div className="settings-grid-3" style={{ marginTop: 14 }}>
          <label className="settings-field">
            <span>OpenAI Text Model</span>
            <input className="admin-mono" value={form.openaiTextModel || ''} onChange={e => setField('openaiTextModel', e.target.value)} />
          </label>
          <label className="settings-field">
            <span>OpenAI Image Model</span>
            <input className="admin-mono" value={form.openaiImageModel || ''} onChange={e => setField('openaiImageModel', e.target.value)} />
          </label>
          <label className="settings-field">
            <span>Anthropic Model</span>
            <input className="admin-mono" value={form.anthropicModel || ''} onChange={e => setField('anthropicModel', e.target.value)} />
          </label>
          <label className="settings-field">
            <span>Gemini Model</span>
            <input className="admin-mono" value={form.geminiModel || ''} onChange={e => setField('geminiModel', e.target.value)} />
          </label>
          <label className="settings-field">
            <span>Gemini Image Model</span>
            <input className="admin-mono" value={form.geminiImageModel || ''} onChange={e => setField('geminiImageModel', e.target.value)} />
          </label>
        </div>
      </section>

      <section className="settings-card">
        <div className="settings-card-header">
          <h2 className="settings-card-title">{t.adminStoryGeneration}</h2>
        </div>
        <div className="settings-grid-3">
          <label className="settings-field">
            <span>{t.adminIterationCap}</span>
            <input
              type="number"
              min="1"
              value={form.defaultIterationCapPerScene || 2}
              onChange={e => setField('defaultIterationCapPerScene', e.target.value)}
            />
            <span className="settings-field-hint">{t.adminIterationCapHint}</span>
          </label>
        </div>
      </section>

      <section className="settings-card">
        <div className="settings-card-header">
          <h2 className="settings-card-title">{t.adminStoryFont}</h2>
        </div>
        <p className="settings-card-help">{t.adminStoryFontHelp}</p>
        <div className="settings-grid">
          <label className="settings-field">
            <span>{t.adminStoryFontLabel}</span>
            <select
              value={form.storyBodyFont || 'cairo'}
              onChange={e => {
                setField('storyBodyFont', e.target.value);
                // Live-apply so the admin's own story previews switch face
                // immediately instead of after a reload.
                document.documentElement.style.setProperty(
                  '--story-font', storyFontCss(e.target.value) || DEFAULT_STORY_FONT_CSS
                );
              }}
              style={{ fontFamily: storyFontCss(form.storyBodyFont) || DEFAULT_STORY_FONT_CSS }}
            >
              {STORY_FONTS.map(f => (
                <option key={f.key} value={f.key} style={{ fontFamily: f.css }}>{f.label}</option>
              ))}
            </select>
            <span className="settings-field-hint">{t.adminStoryFontHint}</span>
          </label>
        </div>
        <p
          dir="rtl"
          style={{
            fontFamily: storyFontCss(form.storyBodyFont) || DEFAULT_STORY_FONT_CSS,
            fontSize: '1.3rem', lineHeight: 2, marginTop: 14, textAlign: 'center',
            background: '#fffdf7', border: '1px solid #ece3cf', borderRadius: 14, padding: '18px 16px',
          }}
        >
          {t.adminStoryFontSample}
        </p>
      </section>

      <section className="settings-card">
        <div className="settings-card-header">
          <h2 className="settings-card-title">{t.adminMasterPrompt}</h2>
          <label className="settings-switch">
            <input
              type="checkbox"
              checked={!!form.storyMasterPromptEnabled}
              onChange={e => setField('storyMasterPromptEnabled', e.target.checked)}
            />
            <span className="settings-switch-track" />
            <span className="settings-switch-label">{form.storyMasterPromptEnabled ? t.adminMasterPromptEnabled : t.adminMasterPromptDisabled}</span>
          </label>
        </div>
        <p className="settings-card-help">
          {t.adminMasterPromptHelp}
        </p>
        <label className="settings-field">
          <span>{t.adminPromptText}</span>
          <textarea
            className="admin-mono"
            rows={14}
            dir="auto"
            style={{ width: '100%', resize: 'vertical', lineHeight: 1.6 }}
            value={form.storyMasterPrompt || ''}
            onChange={e => setField('storyMasterPrompt', e.target.value)}
            placeholder={t.adminPromptPlaceholder}
          />
          <span className="settings-field-hint">
            {t.adminPromptOutputHint}
          </span>
        </label>
      </section>

      <section className="settings-card">
        <div className="settings-card-header">
          <h2 className="settings-card-title">{t.adminProductCatalog}</h2>
          <button type="button" className="admin-btn-secondary" onClick={addProduct}>{t.adminAddProduct}</button>
        </div>
        <p className="settings-card-help">
          {t.adminProductCatalogHelp}
        </p>
        <table className="settings-product-table">
          <thead>
            <tr>
              <th style={{ width: '20%' }}>{t.adminProductKey}</th>
              <th>{t.adminProductName}</th>
              <th style={{ width: '14%' }}>{t.adminProductPrice}</th>
              <th style={{ width: '14%' }}>{t.adminProductCompareAt}</th>
              <th style={{ width: '12%' }}>{t.adminProductCurrency}</th>
              <th style={{ width: '8%' }}></th>
            </tr>
          </thead>
          <tbody>
            {(form.products || []).map((p, i) => (
              <tr key={i}>
                <td>
                  <input className="admin-mono" value={p.key || ''} onChange={e => setProduct(i, 'key', e.target.value)} placeholder="softcover" />
                </td>
                <td>
                  <input value={p.nameAr || ''} onChange={e => setProduct(i, 'nameAr', e.target.value)} placeholder="غلاف ورقي" />
                </td>
                <td>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={(Number(p.priceCents) || 0) / 100}
                    onChange={e => setProduct(i, 'priceCents', Math.round(Number(e.target.value || 0) * 100))}
                  />
                </td>
                <td>
                  {/* Display-only strikethrough anchor; empty/0 = no discount shown */}
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={Number(p.compareAtCents) > 0 ? Number(p.compareAtCents) / 100 : ''}
                    placeholder="—"
                    onChange={e => setProduct(i, 'compareAtCents', Math.round(Number(e.target.value || 0) * 100) || null)}
                  />
                </td>
                <td>
                  <select value={p.currency || 'ILS'} onChange={e => setProduct(i, 'currency', e.target.value)}>
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td>
                  <button type="button" className="admin-btn-danger" onClick={() => removeProduct(i)}>{t.adminDelete}</button>
                </td>
              </tr>
            ))}
            {(!form.products || form.products.length === 0) && (
              <tr><td colSpan={6} className="admin-empty">{t.adminNoProductsYet}</td></tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="settings-card">
        <div className="settings-card-header">
          <h2 className="settings-card-title">{t.adminDeliveryFees}</h2>
        </div>
        <p className="settings-card-help">
          {t.adminDeliveryFeesHelp}
        </p>
        <div className="settings-grid">
          <label className="settings-field">
            <span>{t.adminDeliveryAmount}</span>
            <input
              type="number"
              min="0"
              step="1"
              value={(Number(form.delivery?.amountCents) || 0) / 100}
              onChange={e => setDelivery('amountCents', Math.round(Number(e.target.value || 0) * 100))}
            />
          </label>
          <label className="settings-field">
            <span>{t.adminCurrencyField}</span>
            <select value={form.delivery?.currency || 'ILS'} onChange={e => setDelivery('currency', e.target.value)}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>
      </section>

      <section className="settings-card">
        <div className="settings-card-header">
          <h2 className="settings-card-title">{t.adminGradBanner}</h2>
          <label className="settings-switch">
            <input
              type="checkbox"
              checked={form.gradBannerEnabled !== false}
              onChange={e => setField('gradBannerEnabled', e.target.checked)}
            />
            <span className="settings-switch-track" />
            <span className="settings-switch-label">{form.gradBannerEnabled !== false ? t.adminMasterPromptEnabled : t.adminMasterPromptDisabled}</span>
          </label>
        </div>
        <p className="settings-card-help">
          {t.adminGradBannerHelp}
        </p>
      </section>

      <section className="settings-card">
        <div className="settings-card-header">
          <h2 className="settings-card-title">{t.adminPayFirstTitle}</h2>
          <label className="settings-switch">
            <input
              type="checkbox"
              checked={!!form.payFirstExperiment?.enabled}
              onChange={e => setPayFirst('enabled', e.target.checked)}
            />
            <span className="settings-switch-track" />
            <span className="settings-switch-label">{form.payFirstExperiment?.enabled ? t.adminMasterPromptEnabled : t.adminMasterPromptDisabled}</span>
          </label>
        </div>
        <p className="settings-card-help">
          {t.adminPayFirstHelp}
        </p>
        <div className="settings-grid">
          <label className="settings-field">
            <span>{t.adminPayFirstPercent}</span>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={Number(form.payFirstExperiment?.percent) || 0}
              onChange={e => setPayFirst('percent', Math.max(0, Math.min(100, Math.round(Number(e.target.value || 0)))))}
            />
          </label>
        </div>
      </section>

      <section className="settings-card">
        <div className="settings-card-header">
          <h2 className="settings-card-title">{t.adminPromoBanner}</h2>
          <label className="settings-switch">
            <input
              type="checkbox"
              checked={!!form.promo?.enabled}
              onChange={e => setPromo('enabled', e.target.checked)}
            />
            <span className="settings-switch-track" />
            <span className="settings-switch-label">{form.promo?.enabled ? t.adminMasterPromptEnabled : t.adminMasterPromptDisabled}</span>
          </label>
        </div>
        <p className="settings-card-help">
          {t.adminPromoBannerHelp}
        </p>

        <div className="settings-field" style={{ marginBottom: 12 }}>
          <span>صورة البانر</span>
          {form.promo?.imageUrl ? (
            <img
              src={resolveAsset(form.promo.imageUrl)}
              alt="بانر"
              style={{ display: 'block', maxWidth: '100%', maxHeight: 160, borderRadius: 8, margin: '8px 0' }}
            />
          ) : null}
          <input
            type="file"
            accept="image/*"
            disabled={uploadingBanner}
            onChange={e => uploadBanner(e.target.files?.[0])}
          />
          {uploadingBanner && <span className="settings-card-help">جارٍ الرفع…</span>}
        </div>

        <div className="settings-grid">
          <label className="settings-field">
            <span>سعر العرض (للكتب)</span>
            <input
              type="number"
              min="0"
              step="1"
              value={(Number(form.promo?.bundlePriceCents) || 0) / 100}
              onChange={e => setPromo('bundlePriceCents', Math.round(Number(e.target.value || 0) * 100))}
            />
          </label>
          <label className="settings-field">
            <span>عدد الكتب في المجموعة</span>
            <input
              type="number"
              min="1"
              step="1"
              value={Number(form.promo?.bundleQty) || 2}
              onChange={e => setPromo('bundleQty', Math.max(1, Math.round(Number(e.target.value) || 2)))}
            />
          </label>
          <label className="settings-field">
            <span>نوع المنتج</span>
            <select
              value={form.promo?.bundleProductKey || 'hardcover'}
              onChange={e => setPromo('bundleProductKey', e.target.value)}
            >
              {(form.products || []).map(p => <option key={p.key} value={p.key}>{p.nameAr || p.key}</option>)}
              {!(form.products || []).some(p => p.key === (form.promo?.bundleProductKey || 'hardcover')) && (
                <option value={form.promo?.bundleProductKey || 'hardcover'}>{form.promo?.bundleProductKey || 'hardcover'}</option>
              )}
            </select>
          </label>
          <label className="settings-field">
            <span>يبدأ في</span>
            <input
              type="datetime-local"
              value={toLocalInput(form.promo?.startsAt)}
              onChange={e => setPromo('startsAt', fromLocalInput(e.target.value))}
            />
          </label>
          <label className="settings-field">
            <span>ينتهي في</span>
            <input
              type="datetime-local"
              value={toLocalInput(form.promo?.endsAt)}
              onChange={e => setPromo('endsAt', fromLocalInput(e.target.value))}
            />
          </label>
        </div>
      </section>

      <div className="settings-save-bar">
        <div>
          {error && <span className="settings-save-bar-status error">{error}</span>}
          {!error && savedMsg && <span className="settings-save-bar-status ok">✓ {savedMsg}</span>}
          {!error && !savedMsg && <span className="settings-save-bar-status">التغييرات لا تُحفظ تلقائياً</span>}
        </div>
        <button type="button" className="admin-btn-primary" onClick={save} disabled={saving}>
          {saving ? 'جاري الحفظ…' : 'حفظ كل الإعدادات'}
        </button>
      </div>
    </div>
  );
}
